import { CoachBasic, Media, PlayerAccount, PlayerSearchResponse, Profile, MediaCategory } from "@/interfaces";

import { PlayerSignUpStep, signUpSteps } from "@/services";
import * as IT from "../interfaces";
import { URLS } from "./routes";
import { Player, SocialCoachSession } from "@/services/bedrock";

export const phoneRegExp =
  /^((\\+[1]{1}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;
export const phoneNumberMask = [/[1-9]/, /\d/, /\d/, "-", /\d/, /\d/, /\d/, "-", /\d/, /\d/, /\d/, /\d/];
export const webUrlRegExp = /^(https?:\/\/).*/i;

export function fullNameFrom({ firstName, lastName }: PlayerSearchResponse | PlayerAccount | Profile | Player): string {
  return (firstName + " " || "") + lastName || "";
}

export function extractInitials(...args: (string | null | undefined)[]) {
  const combinedName = args
    .filter(arg => typeof arg === "string" && arg.trim() !== "")
    .join(" ")
    .trim();

  if (!combinedName) {
    return "?";
  }

  const words = combinedName.split(/\s+/);

  if (words.length === 1) {
    return words[0][0].toUpperCase();
  }

  const firstInitial = words[0][0];
  const lastInitial = words[words.length - 1][0];

  return (firstInitial + lastInitial).toUpperCase();
}

export function userFullName({ firstName, lastName }: CoachBasic | Profile | PlayerAccount): string {
  return (firstName ? firstName + " " : "") + (lastName ? lastName : "");
}

export function getFullName(firstName?: string, lastName?: string): string {
  return (firstName ? firstName + " " : "") + (lastName ? lastName : "");
}
export function validEmail(email: string): boolean {
  return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email);
}

export const textWithLimit = (text: string | undefined, limit: number = 0): string => {
  const dots = "...";
  if (!text) {
    return "";
  }
  if (text && text.length > limit) {
    text = text.substring(0, limit) + dots;
  }

  return text;
};

export const getImageAsBase64 = (img: any, callback: any) => {
  const reader = new FileReader();
  reader.addEventListener("load", () => callback(reader.result));
  reader.readAsDataURL(img);
};

export function mediaElement(
  uri: string,
  extension: string,
  category: MediaCategory,
  id: number = -1,
  sortOrder: number = 0
): Media {
  return {
    category,
    extension,
    id,
    sortOrder,
    type: extension,
    uri,
  };
}

export function mediaElementSticket(
  uri: string,
  extension: string,
  category: MediaCategory,
  sortOrder: number = 0
): Media {
  return {
    id: sortOrder,
    category,
    extension,
    sortOrder,
    type: extension,
    uri,
  };
}

export function emptyField(value?: string): boolean {
  if (value === undefined) {
    return true;
  }
  return value.replace(/\s+/g, "").length === 0;
}

export function validateLong(minSize: number, maxSize: number, value?: string): boolean {
  if (value === undefined) {
    return false;
  }
  const valueWithOutSpaces = value.replace(/\s+/g, "");
  return valueWithOutSpaces.length >= minSize && valueWithOutSpaces.length <= maxSize;
}

export const HASHTAG_MAX_LENGTH = 100;

export function removeWhiteSpace(value: string): string {
  return value.replace(/\s+/g, "");
}

export function getStatusText(status: IT.SubscriptionStatus | IT.CoachStatuses | IT.VideoProjectStatus | string) {
  const value = status.valueOf();
  if (value === "PENDING_FOR_APPROVAL") {
    return "PENDING";
  }
  return value.replace(/_/g, " ");
}

export function getSubscriptionType(subscriptionType?: IT.PricePlanTier) {
  if (subscriptionType === undefined) {
    return "";
  }

  switch (subscriptionType) {
    case IT.PricePlanTier.PLAYER_BASIC:
      return "Basic";
    case IT.PricePlanTier.PLAYER_PLUS:
      return "+";
  }
}

export function getLargeSubscriptionType(subscriptionType?: IT.PricePlanTier) {
  if (subscriptionType === undefined) {
    return "";
  }

  switch (subscriptionType) {
    case IT.PricePlanTier.PLAYER_BASIC:
      return "Basic";
    case IT.PricePlanTier.PLAYER_PLUS:
      return "SC+";
  }
}

/**
 * Returns MIME types that aren't "image/" or "video/"
 */
export function getValidFiles(files: File[]): File[] {
  return files.filter(file => {
    return file.type.startsWith("image/") || file.type.startsWith("video/");
  });
}

/**
 * Funtion to know if an image has the valid reels dimensions (Feed returns true always)
 * @param height
 * @param width
 * @param asReel
 * @returns true if the file has valid dimensions
 */
export function validDimensions(height: number, width: number, asReel: boolean = false): boolean {
  // Valid dimensions
  if (asReel) {
    return height >= 960 && height <= 1920 && width >= 540 && width <= 1080;
  } else {
    return true;
  }
}

export function validateMessageLengthInSocialNetworks(message: string | undefined | null): string[] {
  let socialNetworks: string[] = [];
  const socialNetworkRestrictions = [
    { name: "Facebook", maxLength: 49999 },
    { name: "Instagram", maxLength: 2200 },
    { name: "LinkedIn", maxLength: 4000 },
    { name: "Twitter", maxLength: 4000 },
    { name: "Tiktok", maxLength: 2200 },
  ];
  socialNetworkRestrictions.forEach(sn => {
    if (message && message.length > sn.maxLength) {
      socialNetworks.push(sn.name);
    }
  });
  return socialNetworks;
}

export function validAspectRatio(height: number, width: number, asReel: boolean = false): boolean {
  // Valid Aspect Ratios: 16:9 - 4:5, 1:1, 1.91:1, 9:16 and 3:4
  if (asReel) {
    return validReelsAspectRatio(height, width);
  } else {
    const validHorizontalWidth = Math.round((16 * height) / 9);
    const validVerticalWidth = Math.round((4 * height) / 5);
    const validHorizontalSquare = Math.round((1.91 * height) / width);
    const validTallVerticalWidth = Math.round((9 * height) / 16);
    const validMaxFeedVerticalWidth = Math.round((3 * height) / 4);

    if (
      validHorizontalWidth === width ||
      validVerticalWidth === width ||
      height === width ||
      validTallVerticalWidth === width ||
      validMaxFeedVerticalWidth === width ||
      validHorizontalSquare === 1
    ) {
      return true;
    }
    return false;
  }
}

function validReelsAspectRatio(height: number, width: number): boolean {
  // Valid Aspect Ratios: 9:16, 4:5
  const validVerticalWidth = Math.round((9 * height) / 16);
  const validVerticalWidth2 = Math.round((4 * height) / 5);
  if (validVerticalWidth === width || validVerticalWidth2 === width) {
    return true;
  }
  return false;
}

export function redirectPlayerIfSignUpFinished(signUpStep: number | undefined): void {
  if (signUpStep && signUpSteps[signUpStep] === PlayerSignUpStep.PLAYER_BUSINESS_DATA) {
    window.location.assign(URLS.player.dashboard);
  }
}

export function redirectPlayerIfSignUpNotFinished(session: SocialCoachSession | undefined): void {
  if (session?.signUpStep) {
    const playerId = session?.playerId;
    if (playerId) {
      if (session.signUpStep < 4 && sessionStorage.getItem(`skippedOnboarding_${playerId}`) !== "true") {
        const url = getRedirectForPlayer(session!) || URLS.player.dashboard;
        if (url && typeof window !== "undefined") window.location.assign(url);
      }
    }
  }
}

export function getRedirectForPlayer(session: SocialCoachSession): string | undefined {
  const playerId = session?.playerId;
  if (!playerId || !session.signUpStep) return undefined;
  if (playerId) {
    if (sessionStorage.getItem(`skippedOnboarding_${playerId}`) === "true") {
      return undefined;
    }
  }

  if (session.signUpStep <= 3) return URLS.player.firstLogin;
  if (session.signUpStep <= 4) return URLS.player.firstSocialNetworks;
  return URLS.player.dashboard;
}

export function validatePendingAck(vp: IT.AkcElement[] | undefined, role: string): boolean {
  if (vp === undefined) {
    return false;
  }
  return vp.some(akc => akc.ackFlag === false && akc.userRole === role);
}

export function getPlayerAccountFromSession(session: any): PlayerAccount | undefined {
  if (session?.userId == null || !session.firstName || !session.lastName) {
    return undefined;
  }
  return {
    userId: session.userId,
    playerId: session.playerId,
    email: session.email,
    firstName: session.firstName,
    lastName: session.lastName,
    phone: session.phone,
    photoUrl: session.photoUrl,
    title: session.title,
    complianceDisclaimer: session.complianceDisclaimer,
  };
}
